import styled, { css } from "styled-components";

import { IRideStatusIndicatorControl } from "@ryde/controls";
import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  redMui,
  rydeColors,
  rydeColorsGraphsScheme,
  tealMui,
} from "@ryde/theme";

const RIDE_HEADER_TEXT_ITEM_MAX_WIDTH_PX = 210;
const RIDE_HEADER_VEHICLE_TYPE_MAX_WIDTH_PX = 208;
const RIDE_HEADER_SUPPLIER_DRIVER_MAX_WIDTH_PX = 162;

const headerInfoLineOverflowCss = css`
  min-width: 0;
  overflow: hidden;
  width: 100%;

  > * {
    flex-shrink: 0;
  }

  > *:has(.item__content) {
    min-width: 0;
    flex-shrink: 1;
    max-width: ${RIDE_HEADER_TEXT_ITEM_MAX_WIDTH_PX}px;
  }

  > *:has(.item__content) > * {
    display: block;
    min-width: 0;
    max-width: 100%;
  }

  h2 {
    flex-shrink: 0;
    overflow: visible;
  }

  .item__content,
  .item__content.truncate-text {
    display: flex;
    align-items: center;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .item__content .label {
    flex-shrink: 0;
  }

  .item__content .info,
  .item__content .course {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const RideHeaderStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-header",
})`
  width: 100%;
  min-width: 0;
  border-bottom: 1px solid ${greyMui._300};
  box-shadow: 0px 2px 3px rgba(172, 171, 171, 0.2);
  display: flex;
  align-items: center;
  padding-inline-start: 25px;

  .RideDrawer-HeaderTitle {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }

  .RideDrawer-HeaderButtons {
    flex-shrink: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 10px;
    font-size: 12px;
  }
`;

export const HeaderLine1Styled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-header-line1",
})`
  ${headerInfoLineOverflowCss};
  height: 36px;
  display: flex;
  align-items: center;
  padding-top: 11px;
  h2 {
    font-size: 18px;
    color: ${greyMui._800};
    margin-inline-end: 8px;
    font-weight: 600;
    .ride-id {
      padding-inline-start: 7px;
      color: ${greyMui._600};
      font-weight: 700;
    }
    .ide-id-mobile {
      padding-inline-start: 5px;
      color: ${greyMui._600};
      font-weight: 700;
    }
  }

  .ride-header-hub {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    .ride-header-hub-icon {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
    .ride-header-related-hub-link {
      margin-inline-start: 4px;
      color: ${tealMui._700};
      text-decoration: underline;
      cursor: pointer;
      &:hover {
        color: ${tealMui._900};
      }
    }
    .ride-header-related-hub-id {
      margin-inline-start: 4px;
      color: ${greyMui._600};
    }
  }

  label.course svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-inline-start: 0;
  }

  & > :not(:first-child):not(:last-child)::after {
    content: "";
    border-inline-end: 1px solid ${blueGreyMui._300};
    height: 8px;
    margin-inline-start: 8px;
    display: inline-block;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-top: 0;

    h2 {
      margin-inline-end: 0;
    }
  }
`;
export const HeaderLine2Styled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-header-line2",
})`
  ${headerInfoLineOverflowCss};
  height: 30px;
  display: flex;
  align-items: center;
  font-size: 12px;

  > .ride-header-line2-vehicle-type {
    max-width: ${RIDE_HEADER_VEHICLE_TYPE_MAX_WIDTH_PX}px;
  }

  > .ride-header-line2-supplier,
  > .ride-header-line2-driver {
    max-width: ${RIDE_HEADER_SUPPLIER_DRIVER_MAX_WIDTH_PX}px;
  }

  /* Separate each field's value from its label (label already has a leading margin). */
  .info {
    margin-inline-start: 5px;
  }

  .feedbacks-count-label {
    font-weight: 500;
    font-size: 12px;
  }

  .is-compact .feedback-count-icon-container {
    margin-inline-end: 4px;

    svg {
      margin-inline-start: 0;
    }
  }

  /* The shared course icon fills with currentColor, and this line's text is black. */
  .ride-header-course-icon {
    color: ${blueMui.A200};
  }

  .rideTotalDurationLabel {
    color: ${tealMui._700};
  }

  .rideBoardingSegmentDurationLabel {
    color: ${blueMui._700};
  }

  .rideTotalDurationLabel_exceeded {
    color: ${rydeColors.white};
    padding: 1px 2px;
    margin-inline-end: 6px;
    background-color: ${rydeColorsGraphsScheme.Red.Red1};
    border-radius: 3px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    line-height: 1;
    white-space: nowrap;
    &.allowed_by_settings {
      background-color: ${orangeMui._800};
    }
    &.direct_ride_ignored {
      background-color: ${redMui._900};
    }

    svg {
      margin-inline-start: 0;
    }
  }

  .rideBoardingSegmentDurationLabel_exceeded {
    color: ${rydeColors.white};
    padding: 1px 2px;
    margin-inline-end: 6px;
    background-color: ${rydeColorsGraphsScheme.Red.Red1};
    border-radius: 3px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    line-height: 1;
    white-space: nowrap;

    svg {
      margin-inline-start: 0;
    }
  }

  .rideTotalDurationLabel-exclamationIcon {
    margin: 0;
    color: ${rydeColors.white};
    width: 12px;
    height: 12px;
    margin-inline-end: 2px;
  }

  & > :not(:last-child)::after {
    content: "";
    border-inline-end: 1px solid ${blueGreyMui._300};
    height: 8px;
    margin-inline-start: 4px;
    display: inline-block;
  }

  & > *:has(.rideTotalDurationLabel_exceeded)::after,
  & > *:has(.rideBoardingSegmentDurationLabel_exceeded)::after {
    margin-inline-start: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: auto;
    flex-flow: wrap;
    margin-top: 4px;
    padding-inline-start: 8px;
    width: 100%;
  }
`;

export const RideHeaderStatusStyled = styled.label.attrs<IStyledAttributes>({
  "data-e2e": "ride-header-status",
})<IRideStatusIndicatorControl>`
  border-inline-start: 2px solid ${props => props.$leftColor};
  background-color: ${props => props.$bgColor};
  color: ${props => props.color};
  border-radius: 2px;
  padding: 3px 5px;
  span {
    color: ${props => props.color};
  }
`;

export const RideHeaderPriceDropdownStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "ride-header-price-dropdown",
  },
)`
  width: 226px;
  border: 1px solid ${blueGreyMui.A100};
  padding: 12px;
  border-radius: 5px;
  .headline {
    color: ${blueGreyMui._500};
    display: flex;
    align-items: center;
    margin-bottom: 17px;
    font-size: 13px;
    font-weight: 600;

    .priceRateText {
      margin-inline-end: 8px;
    }

    .linkOut {
      cursor: pointer;
      margin-inline-start: auto;
      min-width: fit-content;
      transform: scale(1.2);
    }
  }
  .price_row {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 17px;
    font-weight: 400;
    .price {
      margin-inline-start: auto;
    }
  }
  .total_row {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 700;
    .total {
      margin-inline-start: auto;
    }
  }

  .divider {
    margin-block: 10px;
  }

  .recalculate_price_row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;

    .price_rate_search_logs_btn {
      margin-inline-end: auto;
      padding: 2px;
      color: ${blueGreyMui._600};
    }

    .recalculate_price_text {
      font-size: 13px;
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._500};
    }
  }
`;

export const RideHeaderPriceSupplierDropdownStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-header-price-supplier-dropdown",
  },
)`
  width: 226px;
  border: 2px solid #9c27b0;
  padding: 12px;
  border-radius: 5px;
  box-shadow:
    0 0 8px rgba(156, 39, 176, 0.4),
    0 0 12px rgba(156, 39, 176, 0.2);
  .headline {
    color: ${blueGreyMui._500};
    display: flex;
    align-items: center;
    margin-bottom: 17px;
    font-size: 13px;
    font-weight: 600;

    .priceRateText {
      margin-inline-end: 8px;
    }

    .linkOut {
      cursor: pointer;
      margin-inline-start: auto;
      min-width: fit-content;
      transform: scale(1.2);
    }
  }
  .price_row {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 17px;
    font-weight: 400;
    .price {
      margin-inline-start: auto;
    }
  }
  .total_row {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 700;
    .total {
      margin-inline-start: auto;
    }
  }

  .divider {
    margin-block: 10px;
  }

  .recalculate_price_row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;

    .price_rate_search_logs_btn {
      margin-inline-end: auto;
      padding: 2px;
      color: ${blueGreyMui._600};
    }

    .recalculate_price_text {
      font-size: 13px;
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._500};
    }
  }
`;

export const RideHeaderSupplierTriggerStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "ride-header-supplier-trigger",
  },
)`
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
  max-width: ${RIDE_HEADER_SUPPLIER_DRIVER_MAX_WIDTH_PX}px;
  overflow: hidden;

  .item__content {
    flex: 1 1 0%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .item__content .info {
    flex: 1 1 0%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ride-header-supplier-clock {
    margin-inline-start: 4px;
    margin-inline-end: 0;

    svg {
      margin-inline-start: 0;
    }
  }

  .ride-header-supplier-clock + .item__content .label {
    margin-inline-start: 2px;
  }
`;

export const HeaderLine2SupplierShowIconStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "ride-header-line2-supplier-show-icon",
  },
)`
  display: flex;
  flex-shrink: 0;
  width: 10px;
  height: 10px;

  svg {
    display: flex;
    width: 100%;
    height: 100%;
  }
`;

export const HeaderLine2SupplierSendStatusIconStyled = styled.span.attrs<IStyledAttributes>(
  {
    "data-e2e": "ride-supplier-send-status",
  },
)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: center;
  margin-inline-start: 1px;
  margin-top: 2px;
  line-height: 0;

  svg {
    margin: 0;
    width: 14px;
    height: 14px;
  }
`;

/** Ride type opts out of area-group default supplier — compact hint on header line 2 (after supplier). */
export const HeaderLine2SkipAreaGroupSupplierIconStyled = styled.span.attrs<IStyledAttributes>(
  {
    "data-e2e": "ride-header-line2-skip-area-group-supplier",
  },
)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-inline-start: 2px;
  margin-inline-end: 2px;
  color: ${greyMui._600};

  svg {
    width: 14px;
    height: 14px;
  }
`;
